import editorialStyle from "../../../components/editorial/style.scss";

import { Fragment } from "preact";
import Link, {
  Button,
  ButtonList,
  LargeViewOnly,
  StickyButtons,
  ButtonBar,
  affiliateMerchants,
} from "../../../components/link";
import AffiliateDisclosure from "../../../components/affiliate-disclosure";
import Center from "../../../components/center";
import Editorial from "../../../components/editorial";
import FixedTable from "../../../components/fixed-table";
import Head from "../../../components/head";
import Hero from "../../../components/hero";
import ImageDetailGrid from "../../../components/image-detail-grid";
import ImageSet from "../../../components/image-set";
import Image from "../../../components/image";
import Author from "../../../components/author";
import BluettiCouponCode from "../components/coupon-code";
import NoWrap from "../../../components/no-wrap";
import Warning from "../../../components/warning";
import SlugHeading from "../../../components/slug-heading";
import SlugLink from "../../../components/slug-link";
import Accordion from "../../../components/accordion";
import AccordionPreview from "../../../components/accordion/preview";
import PhotoListItem from "../../../components/photo-list-item";
import Tags from "../../../components/tags";
import ReviewItem from "../../../components/review-item";
import Questions from "../../../components/questions";
import namedRoutes from "../../../settings/routes";
import { lookupByUrl } from "../../../settings/route-data-lookup";

import AffiliateAd from "../../../components/affiliate-ad";

let headerInfo = lookupByUrl(namedRoutes.bluetti.ac200l);

const price = {
  current: 899,
  msrp: 1599,
};

const Page = () => (
  <div>
    <Head data={headerInfo} />
    <Hero
      positionX={0}
      positionY={50}
      background="/images/backgrounds/bluetti-ac200l.webp"
      titlePrefix="BLUETTI"
      title="AC200L"
      subTitle="Vanlife Power Station Review"
    />
    <Editorial>
      <Center>
        <Author
          published={headerInfo.datePublished}
          updated={headerInfo.dateModified}
        />

        <LargeViewOnly>
          <ButtonList size="medium">
            <Button
              size="medium"
              affiliateData={affiliateMerchants.bluetti}
              href="https://www.bluettipower.com/products/ac200l-portable-power-station"
              icon="Bluetti"
            >
              View on BLUETTI
            </Button>
            <Button
              size="medium"
              affiliate="https://amzn.to/3Q6jLIN"
              href="https://www.amazon.com/dp/B0CLGZB3L6/"
              secondary
              icon="Amazon"
            >
              View on Amazon
            </Button>
          </ButtonList>
        </LargeViewOnly>

        <p>
          The <strong>BLUETTI AC200L</strong> delivers a strong mix of capacity,
          fast charging, and high‑voltage solar support at a price that makes it
          appealing for vanlife and RV builds. It serves as a capable 2kWh LFP
          (LiFePO₄) foundation that can scale through Bluetti’s expansion
          batteries and ecosystem accessories, offering reliable AC performance
          and flexible charging options for life on the road. Its approach to
          high‑amp DC output is different from most power stations and relies on
          add‑on modules for higher output, but the core unit remains a solid
          starting point for anyone building a modular off‑grid system.
        </p>

        <AffiliateDisclosure />

        <ImageSet
          size={3}
          shadow
          images={[
            {
              lg: "/images/products/bluetti/ac200l/1920/ac200l-9539.webp",
              sm: "/images/products/bluetti/ac200l/500/ac200l-9539.webp",
              alt: "BLUETTI AC200L: front right",
            },
            {
              lg: "/images/products/bluetti/ac200l/1920/ac200l-9531.webp",
              sm: "/images/products/bluetti/ac200l/500/ac200l-9531.webp",
              alt: "BLUETTI AC200L: front",
            },
            {
              lg: "/images/products/bluetti/ac200l/1920/ac200l-9545.webp",
              sm: "/images/products/bluetti/ac200l/500/ac200l-9545.webp",
              alt: "BLUETTI AC200LL front left with cords",
            },
          ]}
        />

        <Accordion
          items={[
            {
              heading: "Table of Contents",
              content: (
                <ol>
                  <li>
                    <SlugLink>Highlights</SlugLink>
                  </li>
                  <li>
                    <SlugLink>Considerations</SlugLink>
                  </li>
                  <li>
                    <SlugLink>Detailed Comparison</SlugLink>
                    <ol class="upper-alpha">
                      <li>
                        <SlugLink>Full Specifications</SlugLink>
                      </li>
                    </ol>
                  </li>
                  <li>
                    <SlugLink>Photos</SlugLink>
                  </li>
                  <li>
                    <SlugLink>Recommended Setups</SlugLink>
                  </li>
                  <li>
                    <SlugLink>App Control</SlugLink>
                  </li>
                  <li>
                    <SlugLink>Accessories</SlugLink>
                    <ol class="upper-alpha">
                      <li>
                        <SlugLink>Alternator Chargers</SlugLink>
                      </li>
                      <li>
                        <SlugLink>DC Fuse Panel</SlugLink>
                      </li>
                      <li>
                        <SlugLink>Solar Panels</SlugLink>
                      </li>
                      <li>
                        <SlugLink>Extra Batteries</SlugLink>
                      </li>
                      <li>
                        <SlugLink>Other Accessories</SlugLink>
                      </li>
                      <li>
                        <SlugLink>Included Accessories</SlugLink>
                      </li>
                    </ol>
                  </li>
                  <li>
                    <SlugLink>My Verdict</SlugLink>
                  </li>
                </ol>
              ),
            },
          ]}
        />
      </Center>
      <Center>
        <SlugHeading tag="h2">Highlights</SlugHeading>
        <ul>
          <li>
            <strong>Great value:</strong> At ${price.current} you can power your
            adventures without breaking the bank.
          </li>
          <li>
            <strong>High capacity battery:</strong> 2,048Wh of LFP storage,
            expandable up to 12,288Wh, for safe and reliable power that can grow
            as your power needs increase.
          </li>
          <li>
            <strong>High‑voltage solar support:</strong> 12-145V⎓15A (1,200W)
            supports large series solar arrays. Many power stations today max
            out at only 60V, which requires parallel solar connections.
          </li>
          <li>
            <strong>AC output:</strong> 2,400W continuous (3,600W surge) handles
            most vanlife appliances, from induction cooktops to power tools
            allowing you to ditch propane cooktops and use clean renewable
            energy.
          </li>
          <li>
            <strong>Full Charger 2 integration:</strong> One of only three
            BLUETTI power stations that currently support the{" "}
            <Link
              affiliateData={affiliateMerchants.bluetti}
              href="https://www.bluettipower.com/products/bluetti-charger-2-alternator-solar-charger"
            >
              Charger 2
            </Link>{" "}
            bidirectional port, unlocking alternator charging, reverse charging,
            and full DC Hub functionality.
          </li>
          <li>
            <strong>Cigarette lighter port:</strong> A convenient 12V⎓10A port
            for powering a single device such as a fridge or fan.
          </li>
          <li>
            <strong>4x USB ports:</strong> Two USB‑A and two USB‑C ports for
            charging phones, tablets, and small electronics without adapters. A
            few more USB ports would have been nice since so many devices use
            USB charging.
          </li>
          <li>
            <strong>Fast AC charging:</strong> Recharging defaults to 1,440W
            (12A) with pass‑through support, but can be increased to 2,400W
            (20A), making quick top‑ups easy while parked at a friend's place or
            campground. It is recommended to use the 20A accessory cable for
            charging rates above 15A (1,800W).
          </li>
        </ul>

        <AffiliateAd brand="BLUETTI" size="leaderboard" />
      </Center>
      <Center>
        <SlugHeading tag="h2">Considerations</SlugHeading>
        <ul>
          <li>
            <strong>High DC output requires accessories:</strong> If you plan to
            power more than a single DC item, you will also need to purchase a
            BLUETTI{" "}
            <Link
              affiliateData={affiliateMerchants.bluetti}
              href="https://www.bluettipower.com/products/bluetti-charger-2-alternator-solar-charger"
            >
              Charger 2
            </Link>{" "}
            and{" "}
            <Link
              affiliateData={affiliateMerchants.bluetti}
              href="https://www.bluettipower.com/products/dc-hub-for-charger-2"
            >
              DC Hub
            </Link>{" "}
            which increases the system’s overall cost and footprint. When these
            are added though, it supports an impressive 50A DC not seen on other
            brands.
          </li>
          <li>
            <strong>Single solar port:</strong> Equipped with a single solar
            input. If using solar and alternator charging, it's best paired with
            the BLUETTI{" "}
            <Link
              affiliateData={affiliateMerchants.bluetti}
              href="https://www.bluettipower.com/products/bluetti-charger-2-alternator-solar-charger"
            >
              Charger 2
            </Link>{" "}
            which can use its P090D port freeing up the AC200L’s solar port.
            Otherwise you are forced to manual switch between using the port for
            solar or alternator input.
          </li>
          <li>
            <strong>Internal Fan:</strong> The fan is fairly quiet when active,
            with a low-frequency sound that blends into the background.
            {/* By comparison, the fan in Charger 2 is noticeably louder, but can be
            disabled in quiet mode. */}
          </li>
          <li>
            <strong>Capacity:</strong> While the AC200L is a great starting
            point, its 2,048Wh capacity may not be enough for multi-day off-grid
            stays. Adding an expansion battery like the B300K or B500K provides
            significantly more power but also increases your total cost and
            takes up space.
          </li>
          <li>
            <strong>Footprint:</strong> The unit if fairly compact and can be
            placed directly against a wall. However you must leave room on
            either side for airflow.
          </li>
          <li>
            <strong>Cable and port placement:</strong> All input and output
            ports are located on the front and left side of the unit, so be
            aware when determining placement. Some power stations, such as the
            Apex 300, have 90‑degree cables which saves valuable space.
          </li>
          <li>
            <strong>Cold weather use:</strong> Like most power stations, it
            doesn't include internal self‑heating, so use can be restricted if
            internal temperatures drop below 32°F.
          </li>
          <li>
            <strong>Air conditioner:</strong> The system caps out at 50A DC
            (using DC Hub). If you plan to run a DC air conditioner, the{" "}
            <Link
              affiliateData={affiliateMerchants.bluetti}
              href="https://www.bluettipower.com/products/rv5-rv-solar-system"
            >
              BLUETTI RV5
            </Link>{" "}
            (<Link href={namedRoutes.bluetti.rv5}>see review</Link>) or{" "}
            <Link
              affiliateData={affiliateMerchants.ecoflow}
              href="https://us.ecoflow.com/collections/power-kits-collection"
            >
              EcoFlow Power Kit
            </Link>{" "}
            might better suits your high-amperage needs.
          </li>
          <li>
            <strong>UPS performance:</strong> With a 20ms switching speed, some
            sensitive electronics might flicker or restart during a power
            changeover. While this isn't a primary concern for typical vanlife
            appliances, it's something to consider if you plan to run a desktop
            computer or sensitive medical device. Many power stations now
            support 10ms switching speed.
          </li>
          <li>
            <strong>Bluetooth and physical buttons:</strong> For anyone
            installing the AC200L in a cabinet or under a bed, an optional
            remote screen would have been extremely useful. Being able to toggle
            AC and DC power or check capacity without relying on Bluetooth or
            opening a cabinet would make daily use much easier.
          </li>
        </ul>

        <Warning icon={false} prefix="Charger 2 Integration">
          While the AC200L supports the Charger 2 and DC Hub, I have encountered
          a few issues that impact the hands-off reliability for a van build.
          Please see my{" "}
          <Link href={`${namedRoutes.bluetti.charger2.review}#issues`}>
            Charger 2 review
          </Link>{" "}
          for a detailed breakdown of these issues. I'm in direct communication
          with BLUETTI regarding these findings and will update this review as
          they are resolved.
        </Warning>
      </Center>
      <Center>
        <SlugHeading tag="h2">Detailed Comparison</SlugHeading>
        <p>
          BLUETTI{" "}
          <strong>
            <NoWrap>AC200L</NoWrap>
          </strong>{" "}
          vs{" "}
          <strong>
            <NoWrap>Apex 300</NoWrap>
          </strong>{" "}
          comparison table:
        </p>

        <Fragment>
          <FixedTable>
            <table>
              <thead>
                <tr>
                  <th class={editorialStyle.sticky}>Model</th>
                  <th>AC200L</th>
                  <th>Apex 300</th>
                </tr>
              </thead>
              <tbody>
                <tr class={editorialStyle.noHover}>
                  <th class={editorialStyle.sticky}>
                    <strong>Photo</strong>
                  </th>
                  <td>
                    <Image
                      src="/images/products/bluetti/ac200l/front.webp"
                      alt="BLUETTI AC200L"
                      showAlt
                      maxHeight={150}
                    />
                  </td>
                  <td>
                    <Image
                      src="/images/products/bluetti/apex-300/front.webp"
                      alt="BLUETTI Apex 300"
                      showAlt
                      maxHeight={150}
                    />
                  </td>
                </tr>
                <tr class={editorialStyle.noHover}>
                  <th class={editorialStyle.sticky}>
                    <strong>Links</strong>
                  </th>
                  <td>
                    <ButtonBar>
                      <Button
                        size="small"
                        affiliateData={affiliateMerchants.bluetti}
                        href="https://www.bluettipower.com/products/ac200l-portable-power-station"
                        icon="Bluetti"
                      >
                        Store
                      </Button>
                      <Button
                        size="small"
                        external
                        href="https://cdn.shopify.com/s/files/1/0536/3390/8911/files/17.0303.0627-02_AC200L_User_Manual_BLUETTI_PPS011-6D01A2_23.12.06.pdf?v=1702544515"
                        secondary
                      >
                        Manual
                      </Button>
                    </ButtonBar>
                  </td>
                  <td>
                    <ButtonBar>
                      <Button
                        size="small"
                        affiliateData={affiliateMerchants.bluetti}
                        href="https://www.bluettipower.com/products/apex-300-home-battery-backup?id=68804e7e63e18b529ef2604a&variant=47351562174683"
                        icon="Bluetti"
                      >
                        Store
                      </Button>
                      <Button
                        size="small"
                        external
                        href="https://s4.bluettipower.com/bluetti/purchasePageBiz/2025/12/Apex%20300%20User%20%20Manual%20US%20EN-FR-1764555919287-cde9.pdf"
                        secondary
                      >
                        Manual
                      </Button>
                    </ButtonBar>
                  </td>
                </tr>
                <tr>
                  <th class={editorialStyle.sticky}>
                    <strong>Price</strong>
                  </th>
                  <td>
                    <NoWrap>
                      ${price.current}{" "}
                      {price.current < price.msrp ? <s>${price.msrp}</s> : null}
                    </NoWrap>
                  </td>
                  <td>
                    $1,499 <s>$2,199</s>
                  </td>
                </tr>
                <tr class={editorialStyle.rowBorder}>
                  <th class={editorialStyle.sticky}>
                    <strong>Capacity</strong>
                  </th>
                  <td>2,048Wh</td>
                  <td>2,764Wh</td>
                </tr>
                <tr>
                  <th class={editorialStyle.sticky}>
                    <strong>Extra battery</strong>
                  </th>
                  <td>2x</td>
                  <td>6x</td>
                </tr>
                <tr class={editorialStyle.rowBorder}>
                  <th class={editorialStyle.sticky}>
                    <strong>AC output</strong>
                  </th>
                  <td>
                    2,400W total
                    <br />
                    3,600W surge
                  </td>
                  <td>
                    3,840W total
                    <br />
                    7,680W surge
                  </td>
                </tr>
                <tr>
                  <th class={editorialStyle.sticky}>
                    <strong>AC ports</strong>
                  </th>
                  <td>
                    x4 (20A each) <br />
                    x1 (30A)
                  </td>
                  <td>
                    x4 (20A each) <br />
                    x1 (30A) <br />
                    x1 (120/240V, 50A)
                  </td>
                </tr>

                <tr class={editorialStyle.rowBorder}>
                  <th class={editorialStyle.sticky}>
                    <strong>Cigarette Port</strong>
                  </th>
                  <td>12V⎓10A, 100W</td>
                  <td>
                    Add-on <sup>1</sup> <sup>2</sup>
                  </td>
                </tr>
                <tr>
                  <th class={editorialStyle.sticky}>
                    <strong>48V DC Port</strong>
                  </th>
                  <td>8A</td>
                  <td>--</td>
                </tr>
                <tr>
                  <th class={editorialStyle.sticky}>
                    <strong>50A Anderson</strong>
                  </th>
                  <td>
                    Add-on <sup>2</sup>
                  </td>
                  <td>
                    Add-on <sup>1</sup> <sup>2</sup>
                  </td>
                </tr>

                <tr class={editorialStyle.rowBorder}>
                  <th class={editorialStyle.sticky}>
                    <strong>
                      <NoWrap>Solar input 1</NoWrap>
                    </strong>
                  </th>
                  <td>
                    12-145V⎓15A
                    <br />
                    1,200W
                  </td>
                  <td>
                    12-60V⎓20A
                    <br />
                    1,200W
                  </td>
                </tr>
                <tr>
                  <th class={editorialStyle.sticky}>
                    <strong>
                      <NoWrap>Solar input 2</NoWrap>
                    </strong>
                  </th>
                  <td>--</td>
                  <td>
                    12-60V⎓20A
                    <br />
                    1,200W
                  </td>
                </tr>
                <tr>
                  <th class={editorialStyle.sticky}>
                    <strong>AC input</strong>
                  </th>
                  <td>20A 2,400W</td>
                  <td>15A 1,800W</td>
                </tr>
                <tr class={editorialStyle.rowBorder}>
                  <th class={editorialStyle.sticky}>
                    <strong>USB A</strong>
                  </th>
                  <td>2x</td>
                  <td>
                    Add-on <sup>1</sup> <sup>2</sup>
                  </td>
                </tr>
                <tr>
                  <th class={editorialStyle.sticky}>
                    <strong>USB C</strong>
                  </th>
                  <td>2x</td>
                  <td>
                    Add-on <sup>1</sup> <sup>2</sup>
                  </td>
                </tr>
                <tr class={editorialStyle.rowBorder}>
                  <th class={editorialStyle.sticky}>
                    <strong>Size (in)</strong>
                  </th>
                  <td>16.5 × 11 × 14.4"</td>
                  <td>20.7 × 12.9 × 12.6"</td>
                </tr>
                <tr>
                  <th class={editorialStyle.sticky}>
                    <strong>Size (mm)</strong>
                  </th>
                  <td>
                    <NoWrap>420 × 280 × 367 mm</NoWrap>
                  </td>
                  <td>
                    <NoWrap>525 × 327 × 320 mm</NoWrap>
                  </td>
                </tr>
                <tr>
                  <th class={editorialStyle.sticky}>
                    <strong>Weight</strong>
                  </th>
                  <td>
                    <NoWrap>62lbs (28kg)</NoWrap>
                  </td>
                  <td>
                    <NoWrap>84lbs (38kg)</NoWrap>
                  </td>
                </tr>
              </tbody>
            </table>
          </FixedTable>
          <p class="caption">
            <strong>1.</strong> Requires BLUETTI{" "}
            <Link
              affiliateData={affiliateMerchants.bluetti}
              href="https://www.bluettipower.com/products/hub-d1-parallel-box"
            >
              Hub D1
            </Link>
            . <strong>2.</strong> Requires BLUETTI{" "}
            <Link
              affiliateData={affiliateMerchants.bluetti}
              href="https://www.bluettipower.com/products/dc-hub-for-charger-2"
            >
              DC Hub
            </Link>{" "}
            and{" "}
            <Link
              affiliateData={affiliateMerchants.bluetti}
              href="https://www.bluettipower.com/products/bluetti-charger-2-alternator-solar-charger"
            >
              Charger 2
            </Link>{" "}
            connected via bidirectional port.
          </p>
        </Fragment>
        <br />

        <SlugHeading tag="h3">Full Specifications</SlugHeading>
        <Accordion
          headingsAs="h3"
          items={[
            {
              heading: "General",
              content: (
                <Fragment>
                  <ul style="font-size:0.9em;">
                    <li>
                      <strong>Model:</strong> AC200L
                    </li>
                    <li>
                      <strong>Net Weight:</strong> 62.4lbs (28.3kg)
                    </li>
                    <li>
                      <strong>Dimensions:</strong> 16.5 × 11 × 14.4 in (420 ×
                      280 × 366.5 mm)
                    </li>
                    <li>
                      <strong>Warranty period:</strong> 5 years
                    </li>
                    <li>
                      <strong>Certification:</strong> UL2743, UKCA, TELEC, RCM,
                      FCC ID, CE, PSE, NTC
                    </li>
                  </ul>
                </Fragment>
              ),
            },
            {
              heading: "AC Power",
              content: (
                <Fragment>
                  <ul class="none" style="font-size:0.9em;">
                    <li>
                      AC Output:
                      <ul>
                        <li>
                          <strong>Power:</strong> 2,400W in total (Surge Power:
                          3,600W)
                        </li>
                        <li>
                          <strong>Overload Power:</strong>
                          <ul class="circle">
                            <li>2520W~3000W@2min</li>
                            <li>3000W~3600W@5s</li>
                            <li>&gt;3600W@0.5s</li>
                          </ul>
                        </li>
                        <li>
                          <strong>Voltage:</strong> 120VAC
                        </li>
                        <li>
                          <strong>Current:</strong> 20A
                        </li>
                        <li>
                          <strong>Frequency:</strong> 50Hz / 60Hz
                        </li>
                        <li>
                          <strong>AC Outlets (US/CA):</strong>
                          <ul class="circle">
                            <li>4 x 120V/20A Outlets</li>
                            <li>1 x 120V/30A NEMA TT-30</li>
                          </ul>
                        </li>
                        <li>
                          <strong>Inverter Type:</strong> Pure Sine Wave
                        </li>
                      </ul>
                      <hr />
                    </li>
                    <li>
                      AC Input:
                      <ul>
                        <li>
                          <strong>Voltage:</strong> 120VAC
                        </li>
                        <li>
                          <strong>Current:</strong> 20A Max.
                        </li>
                        <li>
                          <strong>Frequency:</strong> 50Hz / 60Hz
                        </li>
                        <li>
                          <strong>UPS:</strong> Switching time ≤20ms
                        </li>
                        <li>
                          <strong>Power:</strong> 2,400W Max. (0%-80% in 60
                          minutes @ 25°C / 77°F)
                        </li>
                        <li>
                          <strong>Pass-through Charging:</strong> Yes
                        </li>
                      </ul>
                      <hr />
                    </li>
                  </ul>
                  <p class="caption">
                    See{" "}
                    <Link
                      external
                      href="https://cdn.shopify.com/s/files/1/0536/3390/8911/files/17.0303.0627-02_AC200L_User_Manual_BLUETTI_PPS011-6D01A2_23.12.06.pdf?v=1702544515"
                    >
                      product manual
                    </Link>{" "}
                    for other countries.
                  </p>
                </Fragment>
              ),
            },
            {
              heading: "DC Power",
              content: (
                <Fragment>
                  <ul class="none" style="font-size:0.9em;">
                    <li>
                      DC Output:
                      <ul>
                        <li>
                          <strong>USB Output:</strong>
                          <ul class="circle">
                            <li>
                              USB-A (×2): QC 3.0: 5VDC / 3A, 9VDC / 2A, 12VDC /
                              1.5A, 18W Each
                            </li>
                            <li>
                              USB-C (×2): PD 3.0: 5/9/12/15/20VDC / 3A, 20VDC /
                              5A
                            </li>
                          </ul>
                        </li>
                        <li>
                          <strong>Cigarette Lighter Port (×1):</strong> 12VDC /
                          10A
                        </li>
                        <li>
                          <strong>48V DC Port - for BLUETTI D40 (×1):</strong>{" "}
                          48VDC / 8A
                        </li>
                      </ul>
                      <hr />
                    </li>
                    <li>
                      DC Input:
                      <ul>
                        <li>
                          <strong>2-pin Aviation Socket:</strong>
                          <ul class="circle">
                            <li>
                              Solar Input: 1,200W Max., VOC 12-145VDC, 15A
                            </li>
                            <li>
                              Car Input: 12/24V from Cigarette Lighter Port
                            </li>
                          </ul>
                        </li>
                      </ul>
                    </li>
                  </ul>
                </Fragment>
              ),
            },
            {
              heading: "Battery",
              content: (
                <Fragment>
                  <ul style="font-size:0.9em;">
                    <li>
                      <strong>Rated Capacity:</strong> 2,048Wh (40Ah)
                    </li>
                    <li>
                      <strong>Battery Chemistry:</strong> Lithium Iron Phosphate
                      (LiFePO4, LFP)
                    </li>
                    <li>
                      <strong>Cycle life:</strong> 3,000+ Cycles to 80% Original
                      Capacity
                    </li>
                    <li>
                      <strong>Shelf-life:</strong> Recharge to 40%-60% Every 3-6
                      Months
                    </li>
                    <li>
                      <strong>Management System:</strong> MPPT Controller, BMS,
                      etc
                    </li>
                  </ul>
                </Fragment>
              ),
            },
            {
              heading: "Environmental Temperature",
              content: (
                <Fragment>
                  <ul style="font-size:0.9em;">
                    <li>
                      <strong>Optimal Operating Temperature:</strong> 68° to
                      86°F (20° to 30°C)
                    </li>
                    <li>
                      <strong>Charging Temperature:</strong> 32°F to 104°F (0°C
                      to 40°C)
                    </li>
                    <li>
                      <strong>Discharging Temperature:</strong> -4°F to 104°F
                      (-20°C to 40°C)
                    </li>
                    <li>
                      <strong>Storage Temperature:</strong>
                      <ul class="circle">
                        <li>Up to 1 month: -4°F to 113°F (-20°C to 45°C)</li>
                        <li>Up to 3 months: -4°F to 86°F (-20°C to 30°C)</li>
                        <li>Up to 12 months: -4°F to 77°F (-20°C to 25°C)</li>
                      </ul>
                    </li>
                    <li>
                      <strong>Working Humidity:</strong> 10% to 90%
                    </li>
                    <li>
                      <strong>Noise:</strong> 50dB Max
                    </li>
                    <li>
                      <strong>Working Altitude:</strong> 6,561ft (2,000m)
                    </li>
                  </ul>
                </Fragment>
              ),
            },
            {
              heading: "Communications",
              content: (
                <Fragment>
                  <ul style="font-size:0.9em;">
                    <li>
                      <strong>Connection:</strong> WiFi and Bluetooth 5.0/5.1
                      <ul class="circle">
                        <li>Transmission Frequency: 125kbps Max</li>
                        <li>RF Transmission Power: +12dBm Max</li>
                        <li>Receiving Sensitivity: -99dBm/1Mbps</li>
                      </ul>
                    </li>
                  </ul>
                </Fragment>
              ),
            },
          ]}
        />
      </Center>
      <Center>
        <SlugHeading tag="h2">Photos</SlugHeading>

        <ImageSet
          size={4}
          shadow
          images={[
            {
              lg: "/images/products/bluetti/ac200l/1920/ac200l-9538.webp",
              sm: "/images/products/bluetti/ac200l/500/ac200l-9538.webp",
              alt: "BLUETTI AC200L: front left",
            },
            {
              lg: "/images/products/bluetti/ac200l/1920/ac200l-9537.webp",
              sm: "/images/products/bluetti/ac200l/500/ac200l-9537.webp",
              alt: "BLUETTI AC200L: left side ports",
            },
            {
              lg: "/images/products/bluetti/ac200l/1920/ac200l-9532.webp?v=2",
              sm: "/images/products/bluetti/ac200l/500/ac200l-9532.webp?v=2",
              alt: "BLUETTI AC200L: back",
            },
            {
              lg: "/images/products/bluetti/ac200l/1920/ac200l-9533.webp",
              sm: "/images/products/bluetti/ac200l/500/ac200l-9533.webp",
              alt: "BLUETTI AC200L: right side",
            },
            {
              lg: "/images/products/bluetti/ac200l/1920/ac200l-9535.webp",
              sm: "/images/products/bluetti/ac200l/500/ac200l-9535.webp",
              alt: "BLUETTI AC200L: left side",
            },
            {
              lg: "/images/products/bluetti/ac200l/1920/ac200l-9539.webp",
              sm: "/images/products/bluetti/ac200l/500/ac200l-9539.webp",
              alt: "BLUETTI AC200L: front right",
            },
            {
              lg: "/images/products/bluetti/ac200l/1920/ac200l-9531.webp",
              sm: "/images/products/bluetti/ac200l/500/ac200l-9531.webp",
              alt: "BLUETTI AC200L: front",
            },
            {
              lg: "/images/products/bluetti/ac200l/1920/ac200l-9545.webp",
              sm: "/images/products/bluetti/ac200l/500/ac200l-9545.webp",
              alt: "BLUETTI AC200L: front left with cords",
            },
          ]}
        />

        {/* <ImageSet
          size={4}
          shadow
          images={[
            {
              lg: "/images/products/bluetti/ac200l/front.webp",
              alt: "BLUETTI AC200L: front",
            },
            {
              lg: "/images/products/bluetti/ac200l/front-left.webp",
              alt: "BLUETTI AC200L: front left",
            },
            {
              lg: "/images/products/bluetti/ac200l/left.webp",
              alt: "BLUETTI AC200L: side ports",
            },
            {
              lg: "/images/products/bluetti/ac200l/front-right.webp",
              alt: "BLUETTI AC200L: front right",
            },
          ]}
        /> */}
      </Center>
      <Center>
        <SlugHeading tag="h2">Recommended Setups</SlugHeading>
        <p>
          Here are a few different AC200L configurations optimized for various
          vanlife needs along with diagrams to help understand power
          connections:
        </p>

        <Accordion
          right
          items={[
            {
              preview: (
                <AccordionPreview
                  headingAs="h3"
                  prefix="10A DC"
                  heading="Minimal DC Output"
                  wide
                  src="/images/diagrams/power-systems/bluetti/bluetti-ac200l_charger-1.webp?v=2"
                />
              ),
              content: (
                <Fragment>
                  <ImageDetailGrid
                    size={2}
                    showAlt
                    shadow
                    images={[
                      {
                        lg: "/images/diagrams/power-systems/bluetti/bluetti-ac200l_charger-1.webp?v=2",
                        alt: "Minimal DC Output: 10A DC",
                      },
                    ]}
                  />
                  <p>
                    Basic setup consisting of a power station, solar panels, and
                    an alternator charger:
                  </p>
                  <ul class="none">
                    <li>
                      <PhotoListItem
                        affiliateData={affiliateMerchants.bluetti}
                        href="https://www.bluettipower.com/products/ac200l-portable-power-station"
                        img="/images/products/bluetti/ac200l/front.webp"
                        title="AC200L"
                        description={
                          <Fragment>
                            Capacity: 2,048Wh <br />
                            AC output: 2,400W
                          </Fragment>
                        }
                        buttonText="BLUETTI"
                        buttonIcon="Bluetti"
                      />
                    </li>
                    <li>
                      <PhotoListItem
                        affiliateData={affiliateMerchants.bluetti}
                        href="https://www.bluettipower.com/products/bluetti-charger-1"
                        img="/images/products/bluetti/charger-1/front.webp"
                        title="Charger 1"
                        description={
                          <Fragment>Alternator input: 560W</Fragment>
                        }
                        buttonText="BLUETTI"
                        buttonIcon="Bluetti"
                      />
                    </li>
                  </ul>
                  <br />

                  <h4>AC Charging</h4>
                  <ul class="none">
                    <li>
                      <PhotoListItem
                        affiliate="https://amzn.to/47MiFrZ"
                        href="https://www.amazon.com/125V-Port-Plug-Heavy-Duty-Waterproof/dp/B0DK5JHC6N/"
                        img="/images/products/assorted/cables/stio-shore-power-20a.webp"
                        title="Shore power plug"
                        description={<Fragment>Rating: 20A</Fragment>}
                        buttonText="Amazon"
                        buttonIcon="Amazon"
                      />
                    </li>
                    <li>
                      <PhotoListItem
                        affiliate="https://amzn.to/4bXTYLI"
                        href="https://www.amazon.com/dp/B0CFPS9JMB/"
                        img="/images/products/assorted/cables/powgrn-25ft-10awg-extension-cord.webp"
                        title="Heavy duty extension cord"
                        description={
                          <Fragment>
                            Length: 25ft <br />
                            Size: 10AWG
                          </Fragment>
                        }
                        buttonText="Amazon"
                        buttonIcon="Amazon"
                      />
                    </li>
                  </ul>
                  <br />

                  <h4>Solar</h4>
                  <p>
                    There are many options for{" "}
                    <Link
                      affiliate="https://amzn.to/4cdy9Yq"
                      href="https://www.amazon.com/s?k=solar+panel&rh=n%3A21513279011%2Cp_72%3A2661618011%2Cp_85%3A2470955011&dc&crid=3N617E010WJMK&qid=1774933419&rnid=2470954011&sprefix=solar+panel+%2Caps%2C349&ref=sr_nr_p_85_1&ds=v1%3AkN5QhWbit8%2BqO%2BGEZW3LsBHOxT8faCas7W3cKWNMW%2BI"
                    >
                      solar panels
                    </Link>
                    , so you will need to determine what works best for your
                    vehicle by balancing size, quality, and price. This
                    configuration supports two separate solar arrays: 1200W for
                    the AC200L and 600W for the Charger 2.
                  </p>
                </Fragment>
              ),
            },
            {
              preview: (
                <AccordionPreview
                  headingAs="h3"
                  prefix="50A DC"
                  heading="High-Performance"
                  wide
                  src="/images/diagrams/power-systems/bluetti/bluetti-ac200l_charger-2.webp?v=2"
                />
              ),
              content: (
                <Fragment>
                  <ImageDetailGrid
                    size={2}
                    showAlt
                    shadow
                    images={[
                      {
                        lg: "/images/diagrams/power-systems/bluetti/bluetti-ac200l_charger-2.webp?v=2",
                        alt: "High-Performance: 50A DC",
                      },
                    ]}
                  />
                  <p>
                    A capable system for those requiring 50A DC output,
                    consisting of a power station, solar panels, an alternator
                    charger, and a DC fuse panel:
                  </p>

                  <Warning icon={false}>
                    This setup enables high-output DC, but be aware of a few
                    current{" "}
                    <Link
                      href={`${namedRoutes.bluetti.charger2.review}#issues`}
                    >
                      issues with the AC200L, Charger 2, and DC Hub
                    </Link>{" "}
                    that impact some scenarios.
                  </Warning>

                  <ul class="none">
                    <li>
                      <PhotoListItem
                        affiliateData={affiliateMerchants.bluetti}
                        href="https://www.bluettipower.com/products/ac200l-portable-power-station"
                        img="/images/products/bluetti/ac200l/front.webp"
                        title="AC200L"
                        description={
                          <Fragment>
                            Capacity: 2,048Wh <br />
                            AC output: 2,400W
                          </Fragment>
                        }
                        buttonText="BLUETTI"
                        buttonIcon="Bluetti"
                      />
                    </li>
                    <li>
                      <PhotoListItem
                        affiliateData={affiliateMerchants.bluetti}
                        href="https://www.bluettipower.com/products/bluetti-charger-2-alternator-solar-charger"
                        img="/images/products/bluetti/charger-2/front.webp"
                        title="Charger 2"
                        description={
                          <Fragment>
                            Alternator input: 800W <br />
                            Solar input: 600W
                          </Fragment>
                        }
                        buttonText="BLUETTI"
                        buttonIcon="Bluetti"
                      />
                    </li>
                  </ul>

                  <h4>DC Output</h4>
                  <ul class="none">
                    <li>
                      <PhotoListItem
                        affiliateData={affiliateMerchants.bluetti}
                        href="https://www.bluettipower.com/products/dc-hub-for-charger-2"
                        img="/images/products/bluetti/charger-2/dc-hub.webp"
                        title="DC Hub"
                        description={
                          <Fragment>
                            700W Total DC Power
                            <br />
                            Ports: USB-C, USB-A, DC5521, Cigarette Lighter, and
                            Anderson SB50
                          </Fragment>
                        }
                        buttonText="BLUETTI"
                        buttonIcon="Bluetti"
                      />
                    </li>
                    <li>
                      <PhotoListItem
                        affiliate="https://amzn.to/4re7lgm"
                        href="https://www.amazon.com/dp/B000THQ0CQ/"
                        img="/images/products/blue-sea-systems/5025.webp"
                        title="Fuse block"
                        description={
                          <Fragment>
                            Model: Blue Sea Systems 5025 <br />
                            Circuits: 6
                          </Fragment>
                        }
                        buttonText="Amazon"
                        buttonIcon="Amazon"
                      />
                    </li>
                    <li>
                      <PhotoListItem
                        affiliate="https://amzn.to/4m2xgW6"
                        href="https://www.amazon.com/dp/B0FH1GDVKL/"
                        img="/images/products/assorted/cables/anderson-sb50-ring-terminal.webp"
                        title="Fuse block"
                        titleSuffix="(connect fuse block to DC Hub)"
                        description={
                          <Fragment>
                            Included fuse size: 50A (
                            <Link
                              affiliate="https://amzn.to/410F15h"
                              href="https://www.amazon.com/dp/B01M6UA7BZ/"
                            >
                              60A
                            </Link>{" "}
                            is preferred)
                            <br />
                            Wire size: 6AWG
                          </Fragment>
                        }
                        buttonText="Amazon"
                        buttonIcon="Amazon"
                      />
                    </li>
                  </ul>
                  <br />

                  <h4>AC Charging</h4>
                  <ul class="none">
                    <li>
                      <PhotoListItem
                        affiliate="https://amzn.to/47MiFrZ"
                        href="https://www.amazon.com/125V-Port-Plug-Heavy-Duty-Waterproof/dp/B0DK5JHC6N/"
                        img="/images/products/assorted/cables/stio-shore-power-20a.webp"
                        title="Shore power plug"
                        description={<Fragment>Rating: 20A</Fragment>}
                        buttonText="Amazon"
                        buttonIcon="Amazon"
                      />
                    </li>
                    <li>
                      <PhotoListItem
                        affiliate="https://amzn.to/4bXTYLI"
                        href="https://www.amazon.com/dp/B0CFPS9JMB/"
                        img="/images/products/assorted/cables/powgrn-25ft-10awg-extension-cord.webp"
                        title="Heavy duty extension cord"
                        description={
                          <Fragment>
                            Length: 25ft <br />
                            Size: 10AWG
                          </Fragment>
                        }
                        buttonText="Amazon"
                        buttonIcon="Amazon"
                      />
                    </li>
                  </ul>
                  <br />

                  <h4>Solar</h4>
                  <p>
                    There are many options for{" "}
                    <Link
                      affiliate="https://amzn.to/4cdy9Yq"
                      href="https://www.amazon.com/s?k=solar+panel&rh=n%3A21513279011%2Cp_72%3A2661618011%2Cp_85%3A2470955011&dc&crid=3N617E010WJMK&qid=1774933419&rnid=2470954011&sprefix=solar+panel+%2Caps%2C349&ref=sr_nr_p_85_1&ds=v1%3AkN5QhWbit8%2BqO%2BGEZW3LsBHOxT8faCas7W3cKWNMW%2BI"
                    >
                      solar panels
                    </Link>
                    , so you will need to determine what works best for your
                    vehicle by balancing size, quality, and price. This
                    configuration supports two separate solar arrays: 1200W for
                    the AC200L and 600W for the Charger 2.
                  </p>
                </Fragment>
              ),
            },
            {
              preview: (
                <AccordionPreview
                  headingAs="h3"
                  prefix="50A DC + extras"
                  heading="High Capacity Setup"
                  wide
                  src="/images/diagrams/power-systems/bluetti/bluetti-ac200l_charger-2_advanced.webp?v=2"
                />
              ),
              content: (
                <Fragment>
                  <ImageDetailGrid
                    size={2}
                    showAlt
                    shadow
                    images={[
                      {
                        lg: "/images/diagrams/power-systems/bluetti/bluetti-ac200l_charger-2_advanced.webp?v=2",
                        alt: "High Capacity Setup: 50A DC + extras",
                      },
                    ]}
                  />
                  <p>
                    High capacity 7,576Wh setup consisting of two power
                    stations, expansion batteries (1-2), multiple solar arrays,
                    an alternator charger, and a DC fuse panel:
                  </p>

                  <Warning icon={false}>
                    This setup enables high-output DC, but be aware of a few
                    current{" "}
                    <Link
                      href={`${namedRoutes.bluetti.charger2.review}#issues`}
                    >
                      issues with the AC200L, Charger 2, and DC Hub
                    </Link>{" "}
                    that impact some scenarios.
                  </Warning>

                  <ul class="none">
                    <li>
                      <PhotoListItem
                        affiliateData={affiliateMerchants.bluetti}
                        href="https://www.bluettipower.com/products/ac200l-portable-power-station"
                        img="/images/products/bluetti/ac200l/front.webp"
                        title="AC200L"
                        description={
                          <Fragment>
                            Capacity: 2,048Wh <br />
                            AC output: 2,400W
                          </Fragment>
                        }
                        buttonText="BLUETTI"
                        buttonIcon="Bluetti"
                      />
                    </li>
                    <li>
                      <PhotoListItem
                        affiliateData={affiliateMerchants.bluetti}
                        href="https://www.bluettipower.com/products/bluetti-charger-2-alternator-solar-charger"
                        img="/images/products/bluetti/charger-2/front.webp"
                        title="Charger 2"
                        description={
                          <Fragment>
                            Alternator input: 800W <br />
                            Solar input: 600W
                          </Fragment>
                        }
                        buttonText="BLUETTI"
                        buttonIcon="Bluetti"
                      />
                    </li>
                    <li>
                      <PhotoListItem
                        affiliateData={affiliateMerchants.bluetti}
                        href="https://www.bluettipower.com/products/b300k?id=673c0fb87410b96647e90c63&variant=45943077208283"
                        img="/images/products/bluetti/b300k/front.webp"
                        title="B300K"
                        titleSuffix="(1 or 2)"
                        description={<Fragment>Capacity: 2,764Wh</Fragment>}
                        buttonText="BLUETTI"
                        buttonIcon="Bluetti"
                      />
                    </li>
                  </ul>
                  <br />

                  <h4>DC Output</h4>
                  <ul class="none">
                    <li>
                      <PhotoListItem
                        affiliateData={affiliateMerchants.bluetti}
                        href="https://www.bluettipower.com/products/dc-hub-for-charger-2"
                        img="/images/products/bluetti/charger-2/dc-hub.webp"
                        title="DC Hub"
                        description={
                          <Fragment>
                            700W Total DC Power
                            <br />
                            Ports: USB-C, USB-A, DC5521, Cigarette Lighter, and
                            Anderson SB50
                          </Fragment>
                        }
                        buttonText="BLUETTI"
                        buttonIcon="Bluetti"
                      />
                    </li>
                    <li>
                      <PhotoListItem
                        affiliate="https://amzn.to/4pdstS7"
                        href="https://www.amazon.com/dp/B001P6FTHC"
                        img="/images/products/blue-sea-systems/5026.webp"
                        title="Fuse block"
                        description={
                          <Fragment>
                            Model: Blue Sea Systems 5026 <br />
                            Circuits: 12
                          </Fragment>
                        }
                        buttonText="Amazon"
                        buttonIcon="Amazon"
                      />
                    </li>
                    <li>
                      <PhotoListItem
                        affiliate="https://amzn.to/4m2xgW6"
                        href="https://www.amazon.com/dp/B0FH1GDVKL/"
                        img="/images/products/assorted/cables/anderson-sb50-ring-terminal.webp"
                        title="Fuse block"
                        titleSuffix="(connect fuse block to DC Hub)"
                        description={
                          <Fragment>
                            Included fuse size: 50A (
                            <Link
                              affiliate="https://amzn.to/410F15h"
                              href="https://www.amazon.com/dp/B01M6UA7BZ/"
                            >
                              60A
                            </Link>{" "}
                            is preferred)
                            <br />
                            Wire size: 6AWG
                          </Fragment>
                        }
                        buttonText="Amazon"
                        buttonIcon="Amazon"
                      />
                    </li>
                  </ul>
                  <br />

                  <h4>AC Charging</h4>
                  <ul class="none">
                    <li>
                      <PhotoListItem
                        affiliate="https://amzn.to/47MiFrZ"
                        href="https://www.amazon.com/125V-Port-Plug-Heavy-Duty-Waterproof/dp/B0DK5JHC6N/"
                        img="/images/products/assorted/cables/stio-shore-power-20a.webp"
                        title="Shore power plug"
                        description={<Fragment>Rating: 20A</Fragment>}
                        buttonText="Amazon"
                        buttonIcon="Amazon"
                      />
                    </li>
                    <li>
                      <PhotoListItem
                        affiliate="https://amzn.to/4bXTYLI"
                        href="https://www.amazon.com/dp/B0CFPS9JMB/"
                        img="/images/products/assorted/cables/powgrn-25ft-10awg-extension-cord.webp"
                        title="Heavy duty extension cord"
                        description={
                          <Fragment>
                            Length: 25ft <br />
                            Size: 10AWG
                          </Fragment>
                        }
                        buttonText="Amazon"
                        buttonIcon="Amazon"
                      />
                    </li>
                  </ul>
                  <br />

                  <h4>Extra Power Station</h4>
                  <p>
                    This can be any power station that supports DC input via an
                    XT60 or MC4 cable. The Charger 2 CHG port provides an output
                    range of 12-56V at up to 24A. I imagine a smaller
                    grab-and-go unit that can be easily disconnected for a
                    picnic table or other adventure, though a larger unit would
                    work just as well.
                  </p>
                  <PhotoListItem
                    affiliateData={affiliateMerchants.bluetti}
                    href="https://www.bluettipower.com/products/elite-30-v2-portable-power-station"
                    img="/images/products/bluetti/elite-30-v2/front.webp"
                    title="Elite 30 V2"
                    description={<Fragment>Capacity: 288Wh</Fragment>}
                    buttonText="BLUETTI"
                    buttonIcon="Bluetti"
                  />

                  <h4>Solar</h4>
                  <p>
                    There are many options for{" "}
                    <Link
                      affiliate="https://amzn.to/4cdy9Yq"
                      href="https://www.amazon.com/s?k=solar+panel&rh=n%3A21513279011%2Cp_72%3A2661618011%2Cp_85%3A2470955011&dc&crid=3N617E010WJMK&qid=1774933419&rnid=2470954011&sprefix=solar+panel+%2Caps%2C349&ref=sr_nr_p_85_1&ds=v1%3AkN5QhWbit8%2BqO%2BGEZW3LsBHOxT8faCas7W3cKWNMW%2BI"
                    >
                      solar panels
                    </Link>
                    , so you will need to determine what works best for your
                    vehicle by balancing size, quality, and price. This
                    configuration supports at least two separate solar arrays:
                    1200W for the AC200L and 600W for the Charger 2 (extra power
                    station may support another if it has two solar inputs).
                  </p>
                </Fragment>
              ),
            },
          ]}
        />

        {/* <p>
          The beauty of these modular configurations is their simplicity. Unlike
          a custom DIY electrical build that requires dozens of hidden extras
          like specialized tools, lugs, and heavy-gauge cables, these systems
          consist of just a few main components. This allows you to calculate an
          accurate upfront cost without the unexpected expenses or delays
          commonly found in traditional van builds.
        </p> */}
      </Center>
      <Center>
        <SlugHeading tag="h2">App Control</SlugHeading>
        <p>
          The BLUETTI AC200L pairs with the BLUETTI app to allow for device
          monitoring and customization of default settings. The app is available
          on{" "}
          <Link
            external
            href="https://apps.apple.com/us/app/bluetti/id1550568336"
          >
            iOS
          </Link>{" "}
          and{" "}
          <Link
            external
            href="https://play.google.com/store/apps/details?id=net.poweroak.bluetticloud&pli=1"
          >
            Android
          </Link>
          . The screenshots below show all currently available options, ordered
          exactly as they appear in the settings menu.
        </p>
        <ul>
          <li>
            <strong>App Design:</strong> The app is generally good, but small
            design tweaks would help. Some naming is unclear, tooltips are
            missing where they would be useful, and a few common settings are
            buried in the menus.
          </li>
          <li>
            <strong>Grouped Toggle:</strong> The device allows separate control
            of DC and USB, but the app merges them into one toggle, removing the
            ability to control them independently.
          </li>
          <li>
            <strong>Missing Information:</strong> Currently, the system does not
            display Charger 2 input or DC Hub output on the device screen or
            within the app, leaving users in the dark regarding total power
            flow.
          </li>
          <li>
            <strong>Vehicle Integration:</strong> The lack of CarPlay and
            Android Auto support is a missed opportunity for a device often used
            in van-life and overlanding setups where hands-free monitoring is
            important.
          </li>
        </ul>

        <ImageSet
          size={6}
          maxShow={6}
          //shadow={true}
          border
          images={[
            {
              lg: "/images/products/bluetti/ac200l/screenshots/lg/20260402_233451000.webp",
              sm: "/images/products/bluetti/ac200l/screenshots/sm/20260402_233451000.webp",
              alt: "",
            },
            {
              lg: "/images/products/bluetti/ac200l/screenshots/lg/20260401_204445000.webp",
              sm: "/images/products/bluetti/ac200l/screenshots/sm/20260401_204445000.webp",
              alt: "",
            },
            {
              lg: "/images/products/bluetti/ac200l/screenshots/lg/20260401_204542000.webp",
              sm: "/images/products/bluetti/ac200l/screenshots/sm/20260401_204542000.webp",
              alt: "",
            },
            {
              lg: "/images/products/bluetti/ac200l/screenshots/lg/20260401_204602000.webp",
              sm: "/images/products/bluetti/ac200l/screenshots/sm/20260401_204602000.webp",
              alt: "",
            },
            {
              lg: "/images/products/bluetti/ac200l/screenshots/lg/20260401_205019000.webp",
              sm: "/images/products/bluetti/ac200l/screenshots/sm/20260401_205019000.webp",
              alt: "",
            },
            {
              lg: "/images/products/bluetti/ac200l/screenshots/lg/20260401_204610000.webp",
              sm: "/images/products/bluetti/ac200l/screenshots/sm/20260401_204610000.webp",
              alt: "",
            },
            {
              lg: "/images/products/bluetti/ac200l/screenshots/lg/20260401_204635000.webp",
              sm: "/images/products/bluetti/ac200l/screenshots/sm/20260401_204635000.webp",
              alt: "",
            },
            {
              lg: "/images/products/bluetti/ac200l/screenshots/lg/20260401_204647000.webp",
              sm: "/images/products/bluetti/ac200l/screenshots/sm/20260401_204647000.webp",
              alt: "",
            },
            {
              lg: "/images/products/bluetti/ac200l/screenshots/lg/20260401_204655000.webp",
              sm: "/images/products/bluetti/ac200l/screenshots/sm/20260401_204655000.webp",
              alt: "",
            },
            {
              lg: "/images/products/bluetti/ac200l/screenshots/lg/20260401_204704000.webp",
              sm: "/images/products/bluetti/ac200l/screenshots/sm/20260401_204704000.webp",
              alt: "",
            },
            {
              lg: "/images/products/bluetti/ac200l/screenshots/lg/20260401_204725000.webp",
              sm: "/images/products/bluetti/ac200l/screenshots/sm/20260401_204725000.webp",
              alt: "",
            },
            {
              lg: "/images/products/bluetti/ac200l/screenshots/lg/20260401_204733000.webp",
              sm: "/images/products/bluetti/ac200l/screenshots/sm/20260401_204733000.webp",
              alt: "",
            },
            {
              lg: "/images/products/bluetti/ac200l/screenshots/lg/20260401_204921000.webp",
              sm: "/images/products/bluetti/ac200l/screenshots/sm/20260401_204921000.webp",
              alt: "",
            },
            {
              lg: "/images/products/bluetti/ac200l/screenshots/lg/20260401_204924000.webp",
              sm: "/images/products/bluetti/ac200l/screenshots/sm/20260401_204924000.webp",
              alt: "",
            },
            {
              lg: "/images/products/bluetti/ac200l/screenshots/lg/20260401_204931000.webp",
              sm: "/images/products/bluetti/ac200l/screenshots/sm/20260401_204931000.webp",
              alt: "",
            },
            {
              lg: "/images/products/bluetti/ac200l/screenshots/lg/20260401_204933000.webp",
              sm: "/images/products/bluetti/ac200l/screenshots/sm/20260401_204933000.webp",
              alt: "",
            },
            {
              lg: "/images/products/bluetti/ac200l/screenshots/lg/20260401_204938000.webp",
              sm: "/images/products/bluetti/ac200l/screenshots/sm/20260401_204938000.webp",
              alt: "",
            },
            {
              lg: "/images/products/bluetti/ac200l/screenshots/lg/20260401_204945000.webp",
              sm: "/images/products/bluetti/ac200l/screenshots/sm/20260401_204945000.webp",
              alt: "",
            },
            {
              lg: "/images/products/bluetti/ac200l/screenshots/lg/20260401_204948000.webp",
              sm: "/images/products/bluetti/ac200l/screenshots/sm/20260401_204948000.webp",
              alt: "",
            },
            {
              lg: "/images/products/bluetti/ac200l/screenshots/lg/20260401_204952000.webp",
              sm: "/images/products/bluetti/ac200l/screenshots/sm/20260401_204952000.webp",
              alt: "",
            },
            {
              lg: "/images/products/bluetti/ac200l/screenshots/lg/20260401_205007000.webp",
              sm: "/images/products/bluetti/ac200l/screenshots/sm/20260401_205007000.webp",
              alt: "",
            },
            {
              lg: "/images/products/bluetti/ac200l/screenshots/lg/20260401_205025000.webp",
              sm: "/images/products/bluetti/ac200l/screenshots/sm/20260401_205025000.webp",
              alt: "",
            },
            {
              lg: "/images/products/bluetti/ac200l/screenshots/lg/20260401_205031000.webp",
              sm: "/images/products/bluetti/ac200l/screenshots/sm/20260401_205031000.webp",
              alt: "",
            },
            {
              lg: "/images/products/bluetti/ac200l/screenshots/lg/20260401_205035000.webp",
              sm: "/images/products/bluetti/ac200l/screenshots/sm/20260401_205035000.webp",
              alt: "",
            },
            {
              lg: "/images/products/bluetti/ac200l/screenshots/lg/20260401_205038000.webp",
              sm: "/images/products/bluetti/ac200l/screenshots/sm/20260401_205038000.webp",
              alt: "",
            },
            {
              lg: "/images/products/bluetti/ac200l/screenshots/lg/20260401_205042000.webp",
              sm: "/images/products/bluetti/ac200l/screenshots/sm/20260401_205042000.webp",
              alt: "",
            },
            {
              lg: "/images/products/bluetti/ac200l/screenshots/lg/20260401_205049000.webp",
              sm: "/images/products/bluetti/ac200l/screenshots/sm/20260401_205049000.webp",
              alt: "",
            },
            {
              lg: "/images/products/bluetti/ac200l/screenshots/lg/20260402_233639000.webp",
              sm: "/images/products/bluetti/ac200l/screenshots/sm/20260402_233639000.webp",
              alt: "",
            },
            {
              lg: "/images/products/bluetti/ac200l/screenshots/lg/20260401_205102000.webp",
              sm: "/images/products/bluetti/ac200l/screenshots/sm/20260401_205102000.webp",
              alt: "",
            },
            {
              lg: "/images/products/bluetti/ac200l/screenshots/lg/20260401_205109000.webp",
              sm: "/images/products/bluetti/ac200l/screenshots/sm/20260401_205109000.webp",
              alt: "",
            },
            //PRO:Mode
            {
              lg: "/images/products/bluetti/ac200l/screenshots/lg/20260401_205234000.webp",
              sm: "/images/products/bluetti/ac200l/screenshots/sm/20260401_205234000.webp",
              alt: "",
            },
            {
              lg: "/images/products/bluetti/ac200l/screenshots/lg/20260401_205249000.webp",
              sm: "/images/products/bluetti/ac200l/screenshots/sm/20260401_205249000.webp",
              alt: "",
            },
            {
              lg: "/images/products/bluetti/ac200l/screenshots/lg/20260401_205255000.webp",
              sm: "/images/products/bluetti/ac200l/screenshots/sm/20260401_205255000.webp",
              alt: "",
            },

            {
              lg: "/images/products/bluetti/ac200l/screenshots/lg/20260401_205128000.webp",
              sm: "/images/products/bluetti/ac200l/screenshots/sm/20260401_205128000.webp",
              alt: "",
            },
            {
              lg: "/images/products/bluetti/ac200l/screenshots/lg/20260401_205136000.webp",
              sm: "/images/products/bluetti/ac200l/screenshots/sm/20260401_205136000.webp",
              alt: "",
            },
            {
              lg: "/images/products/bluetti/ac200l/screenshots/lg/20260401_205145000.webp",
              sm: "/images/products/bluetti/ac200l/screenshots/sm/20260401_205145000.webp",
              alt: "",
            },
            {
              lg: "/images/products/bluetti/ac200l/screenshots/lg/20260401_205154000.webp",
              sm: "/images/products/bluetti/ac200l/screenshots/sm/20260401_205154000.webp",
              alt: "",
            },
            {
              lg: "/images/products/bluetti/ac200l/screenshots/lg/20260401_205328000.webp",
              sm: "/images/products/bluetti/ac200l/screenshots/sm/20260401_205328000.webp",
              alt: "",
            },
            {
              lg: "/images/products/bluetti/ac200l/screenshots/lg/20260401_205337000.webp",
              sm: "/images/products/bluetti/ac200l/screenshots/sm/20260401_205337000.webp",
              alt: "",
            },
            {
              lg: "/images/products/bluetti/ac200l/screenshots/lg/20260402_233518000.webp",
              sm: "/images/products/bluetti/ac200l/screenshots/sm/20260402_233518000.webp",
              alt: "",
            },
            {
              lg: "/images/products/bluetti/ac200l/screenshots/lg/20260402_233458000.webp",
              sm: "/images/products/bluetti/ac200l/screenshots/sm/20260402_233458000.webp",
              alt: "",
            },
            {
              lg: "/images/products/bluetti/ac200l/screenshots/lg/20260401_205559000.webp",
              sm: "/images/products/bluetti/ac200l/screenshots/sm/20260401_205559000.webp",
              alt: "",
            },
            {
              lg: "/images/products/bluetti/ac200l/screenshots/lg/20260401_205607000.webp",
              sm: "/images/products/bluetti/ac200l/screenshots/sm/20260401_205607000.webp",
              alt: "",
            },
            {
              lg: "/images/products/bluetti/ac200l/screenshots/lg/20260401_205510000.webp",
              sm: "/images/products/bluetti/ac200l/screenshots/sm/20260401_205510000.webp",
              alt: "",
            },
            {
              lg: "/images/products/bluetti/ac200l/screenshots/lg/20260401_205515000.webp",
              sm: "/images/products/bluetti/ac200l/screenshots/sm/20260401_205515000.webp",
              alt: "",
            },
            {
              lg: "/images/products/bluetti/ac200l/screenshots/lg/20260401_205631000.webp",
              sm: "/images/products/bluetti/ac200l/screenshots/sm/20260401_205631000.webp",
              alt: "",
            },
          ]}
        />

        <p class="caption-left">
          The app displays the device serial number on multiple main screens
          rather than a single settings page. This makes it far too easy to
          accidentally leak private hardware info and makes capturing clean,
          shareable screenshots a chore.
        </p>
      </Center>
      <Center>
        <SlugHeading tag="h2">Accessories</SlugHeading>

        <SlugHeading tag="h3">Alternator Chargers</SlugHeading>
        <p>
          Alternator charging is essential for vanlife, offering fast, reliable
          recharging while driving. It often outperforms solar and isn’t
          dependent on sunshine or good weather. BLUETTI currently offers two
          alternator chargers:{" "}
          <Link
            affiliateData={affiliateMerchants.bluetti}
            href="https://www.bluettipower.com/products/bluetti-charger-2-alternator-solar-charger"
          >
            Charger 2
          </Link>{" "}
          and{" "}
          <Link
            affiliateData={affiliateMerchants.bluetti}
            href="https://www.bluettipower.com/products/bluetti-charger-1"
          >
            Charger 1
          </Link>
          . These units pull power from your vehicle to charge your power
          station, similar to a DC-DC charger.
        </p>
        <ul class="none">
          <li>
            <ReviewItem href={namedRoutes.bluetti.charger2.review} />
          </li>
          <li>
            <ReviewItem href={namedRoutes.bluetti.charger1.review} />
          </li>
        </ul>

        <p>
          Not sure it’s the best fit? My{" "}
          <Link href={namedRoutes.alternatorChargers}>
            alternator charger guide
          </Link>{" "}
          covers all the popular brands.
        </p>
        <br />

        <SlugHeading tag="h3">DC Fuse Panel</SlugHeading>
        <p>
          While the AC200L features a standard 12V/10A cigarette port, you can
          unlock its full <strong>50A DC</strong> output to run your entire van
          (fridge, diesel heater, lights) by adding the following components:
        </p>

        <Accordion
          headingsAs="h4"
          right
          items={[
            {
              preview: (
                <AccordionPreview
                  headingAs="h4"
                  prefix="Alternator Charger"
                  heading="BLUETTI Charger 2"
                  src="/images/products/bluetti/charger-2/front.webp"
                />
              ),
              content: (
                <Fragment>
                  <p>
                    The AC200L can be charged from your vehicle using Bluetti’s
                    Charger 2, which provides up to 800W of alternator charging
                    through the bidirectional port. This is the recommended
                    method for vanlife because it supports full DC Hub
                    functionality and avoids tying up the solar input.
                  </p>

                  <ReviewItem href={namedRoutes.bluetti.charger2.review} />

                  <ButtonList>
                    <Button
                      affiliateData={affiliateMerchants.bluetti}
                      href="https://www.bluettipower.com/products/bluetti-charger-2-alternator-solar-charger"
                      icon="Bluetti"
                    >
                      Buy from BLUETTI
                    </Button>
                    <Button
                      affiliate="https://amzn.to/4q6J9LD"
                      href="https://www.amazon.com/dp/B0FS7C11QG/"
                      icon="Amazon"
                      secondary
                    >
                      Buy on Amazon
                    </Button>
                  </ButtonList>
                </Fragment>
              ),
            },
            {
              preview: (
                <AccordionPreview
                  headingAs="h4"
                  prefix="DC Output"
                  heading="BLUETTI DC Hub"
                  src="/images/products/bluetti/charger-2/dc-hub.webp"
                />
              ),
              content: (
                <Fragment>
                  <p>
                    The BLUETTI DC Hub paired with the BLUETTI Charger 2 unlocks
                    the AC200L’s full 50A DC output capability, adding USB
                    ports, DC5521 ports, a cigarette port, and a 50A Anderson
                    SB50 port. This is the required accessory if you plan to run
                    multiple DC loads such as a fridge, heater, lights, or fans.
                  </p>
                  <Warning prefix="Beware">
                    The DC Hub and the BLUETTI{" "}
                    <Link
                      affiliateData={affiliateMerchants.bluetti}
                      href="https://www.bluettipower.com/products/hub-d1-parallel-box"
                    >
                      Hub D1 for the Apex 300
                    </Link>{" "}
                    are different products.
                  </Warning>
                  <ButtonList>
                    <Button
                      affiliateData={affiliateMerchants.bluetti}
                      href="https://www.bluettipower.com/products/dc-hub-for-charger-2"
                      icon="Bluetti"
                    >
                      Buy from BLUETTI
                    </Button>
                  </ButtonList>
                </Fragment>
              ),
            },
            {
              preview: (
                <AccordionPreview
                  headingAs="h4"
                  prefix="Fuse block"
                  heading="Blue Sea Systems 5026 Fuse Block"
                  src="/images/products/blue-sea-systems/5026.webp"
                />
              ),
              content: (
                <Fragment>
                  <p>
                    There is also a 6 circuit model available (
                    <Link
                      affiliate="https://amzn.to/43tntjR"
                      href="https://www.amazon.com/dp/B000THQ0CQ"
                    >
                      5025
                    </Link>
                    ), but it's nice to have room to add extra circuits in the
                    future.
                  </p>
                  <p>
                    <strong>Model:</strong> 5026
                    <br />
                    <strong>Circuits:</strong> 12
                    <br />
                    <strong>Max Amperage:</strong> 30A per circuit (100A total)
                    <br />
                    <strong>Max Voltage:</strong> 32V DC
                    <br />
                    <strong>Fuse Type:</strong> ATO® or ATC®
                  </p>
                  <p>
                    You will also need to purchase the appropriate fuses to
                    match the wiring of your circuits. These can be purchased{" "}
                    <Link
                      affiliate="https://amzn.to/4oLUFvA"
                      href="https://www.amazon.com/dp/B00FLZRJJO"
                    >
                      individually
                    </Link>{" "}
                    or in a{" "}
                    <Link
                      affiliate="https://amzn.to/4r0jiGd"
                      href="https://www.amazon.com/dp/B00CQ8ILFA"
                    >
                      multi-pack
                    </Link>
                    . Be sure to use a reputable brand as these are used to
                    prevent an electrical fire.
                  </p>
                  <ButtonList>
                    <Button
                      affiliate="https://amzn.to/4qXtEXd"
                      href="https://www.amazon.com/dp/B001P6FTHC"
                      icon="Amazon"
                      secondary
                    >
                      Buy on Amazon
                    </Button>
                  </ButtonList>
                </Fragment>
              ),
            },
            {
              preview: (
                <AccordionPreview
                  headingAs="h4"
                  prefix="Anderson SB50 to ring terminal"
                  heading="6 AWG Cable"
                  src="/images/products/assorted/cables/anderson-sb50-ring-terminal.webp"
                />
              ),
              content: (
                <Fragment>
                  <p>
                    This cable allows you to safely connect a DC fuse block to
                    the DC Hub’s Anderson SB50 port without making your own
                    cable. While the DC Hub includes an SB50 connector, you
                    would normally have to supply the cable, ring terminals, and
                    fuses yourself. This pre-made kit saves you from needing
                    specialized crimping tools. I recommend using a{" "}
                    <Link
                      affiliate="https://amzn.to/410F15h"
                      href="https://www.amazon.com/dp/B01M6UA7BZ/"
                    >
                      reputable 60A MEGA fuse
                    </Link>{" "}
                    to prevent nuisance blows instead of the included 50A. Since
                    the DC Hub has its own overcurrent protection, a 60A fuse is
                    a safe and reliable choice.
                  </p>

                  <p>
                    <strong>Length:</strong> 23in
                    <br />
                    <strong>Gauge:</strong> 6AWG
                    <br />
                    <strong>Max Amperage:</strong> 50A (comes with 50A fuse)
                    <br />
                    <strong>Fuse Type:</strong> MEGA
                  </p>
                  <ButtonList>
                    <Button
                      affiliate="https://amzn.to/4m2xgW6"
                      href="https://www.amazon.com/dp/B0FH1GDVKL/"
                      icon="Amazon"
                      secondary
                    >
                      Buy on Amazon
                    </Button>
                  </ButtonList>
                </Fragment>
              ),
            },
          ]}
        />
        <br />

        <SlugHeading tag="h3">Solar Panels</SlugHeading>
        <p>
          BLUETTI offers{" "}
          <Link
            affiliateData={affiliateMerchants.bluetti}
            href="https://www.bluettipower.com/collections/solar-panels"
          >
            portable solar panels
          </Link>
          , but you may prefer rigid panels for a permanent roof mount. You can
          use any reputable brand that fits your vehicle, provided you stay
          within the voltage limits. Wiring is key here: you must check your
          panel specs to decide if you should wire them in series or parallel to
          stay within the AC200L's limits (12-145V⎓15A, 1,200W).
        </p>
        <p>
          <strong>Pro Tip:</strong> If you pair the unit with the Charger 2, you
          gain an additional 600W solar port. This is ideal for vanlife because
          it allows you to run a large series-wired array on your roof (1,200W)
          while keeping the Charger 2's port available for a separate portable
          "ground array" (600W).
        </p>

        <Accordion
          headingsAs="h4"
          items={[
            {
              heading: "Solar Array Configuration",
              content: (
                <Fragment>
                  <p>
                    Solar panel setups often involve arrays of multiple panels
                    using series, parallel, or series-parallel wiring. Series
                    connections add voltage but can suffer significant power
                    drops if even one panel is partially shaded. Parallel wiring
                    adds current (amperage), allowing unshaded panels to still
                    produce power, ideal for patchy sunlight. Series-parallel
                    setups combine both approaches.
                  </p>
                  <h5>Calculating Solar Array Output</h5>
                  <p>
                    To calculate the total voltage and amperage of your solar
                    array, you'll use the Open Circuit Voltage (Voc) and Short
                    Circuit Current (Isc) listed on your panels.
                  </p>
                  <p>For panels wired in series:</p>
                  <p style="padding:0.6em 1em; background: #ddd; border-radius: 6px;">
                    Total Array Voltage = Voc_panel1 + Voc_panel2 + ...
                    <br />
                    Total Array Amperage = Isc_of_a_single_panel
                  </p>
                  <p>For panels wired in parallel:</p>
                  <p style="padding:0.6em 1em; background: #ddd; border-radius: 6px;">
                    Total Array Voltage = Voc_of_a_single_panel
                    <br />
                    Total Array Amperage = Isc_panel1 + Isc_panel2 + ...
                  </p>
                  <p>
                    In a series-parallel setup, you'll first calculate the
                    voltage of each series string, then add the current of those
                    strings together if they're connected in parallel.
                  </p>
                </Fragment>
              ),
            },
          ]}
        />
        <ButtonList>
          <Button
            affiliate="https://amzn.to/4cdy9Yq"
            href="https://www.amazon.com/s?k=solar+panel&rh=n%3A21513279011%2Cp_72%3A2661618011%2Cp_85%3A2470955011&dc&crid=3N617E010WJMK&qid=1774933419&rnid=2470954011&sprefix=solar+panel+%2Caps%2C349&ref=sr_nr_p_85_1&ds=v1%3AkN5QhWbit8%2BqO%2BGEZW3LsBHOxT8faCas7W3cKWNMW%2BI"
            icon="Amazon"
            secondary
          >
            Search on Amazon
          </Button>
        </ButtonList>
        <br />

        <SlugHeading tag="h3">Extra Batteries</SlugHeading>
        <p>Supports the following expansion batteries:</p>
        <ul class="none">
          <li>
            <PhotoListItem
              affiliateData={affiliateMerchants.bluetti}
              href="https://www.bluettipower.com/products/b500k-expansion-battery?id=69118a354abc44066f33eb5b&variant=47914931847387"
              img="/images/products/bluetti/b500k/front.webp"
              title="B500K"
              titleSuffix="(supports 2x)"
              description={<Fragment>5,120Wh</Fragment>}
              buttonText="BLUETTI"
              buttonIcon="Bluetti"
            />
          </li>
          <li>
            <PhotoListItem
              affiliateData={affiliateMerchants.bluetti}
              href="https://www.bluettipower.com/products/b300k?id=673c0fb87410b96647e90c63&variant=45943077208283"
              img="/images/products/bluetti/b300k/front.webp"
              title="B300K"
              titleSuffix="(supports 2x)"
              description={<Fragment>2,764Wh</Fragment>}
              buttonText="BLUETTI"
              buttonIcon="Bluetti"
            />
          </li>
        </ul>
        <Accordion
          items={[
            {
              heading: "Discontinued battery options",
              content: (
                <ul class="none">
                  <li>
                    <PhotoListItem
                      affiliateData={affiliateMerchants.bluetti}
                      href="https://www.bluettipower.com/products/b300"
                      img="/images/products/bluetti/b300/front.webp"
                      title="B300"
                      titleSuffix="(supports 2x)"
                      description={<Fragment>3,072Wh</Fragment>}
                      buttonText="BLUETTI"
                      buttonIcon="Bluetti"
                    />
                  </li>
                  <li>
                    <PhotoListItem
                      affiliateData={affiliateMerchants.bluetti}
                      href="https://www.bluettipower.com/products/b210"
                      img="/images/products/bluetti/b210/front.webp"
                      title="B210"
                      titleSuffix="(supports 2x)"
                      description={<Fragment>2,150Wh</Fragment>}
                      buttonText="BLUETTI"
                      buttonIcon="Bluetti"
                    />
                  </li>
                  <li>
                    <PhotoListItem
                      affiliateData={affiliateMerchants.bluetti}
                      href="https://www.bluettipower.com/products/b230-battery-pack"
                      img="/images/products/bluetti/b230/front.webp"
                      title="B230"
                      titleSuffix="(supports 1x)"
                      description={<Fragment>2,048Wh</Fragment>}
                      buttonText="BLUETTI"
                      buttonIcon="Bluetti"
                    />
                  </li>
                </ul>
              ),
            },
          ]}
        />
        <br />

        <SlugHeading tag="h3">Other Accessories</SlugHeading>
        <ul class="none">
          <li>
            <PhotoListItem
              affiliateData={affiliateMerchants.bluetti}
              href="https://www.bluettipower.com/products/mc4-solar-y-branch-parallel-cable?variant=48179097043163"
              img="/images/products/bluetti/solar-parallel-connection-cable.webp"
              title="Solar Parallel Connection Cable"
              description={
                <Fragment>Connects solar panels in parallel</Fragment>
              }
              buttonText="BLUETTI"
              buttonIcon="Bluetti"
            />
          </li>
          <li>
            <PhotoListItem
              affiliateData={affiliateMerchants.bluetti}
              href="https://www.bluettipower.com/products/dc-hub-for-charger-2"
              img="/images/products/bluetti/charger-2/dc-hub.webp"
              title="DC Hub"
              titleSuffix="(requires Charger 2)"
              description={
                <Fragment>
                  700W Total DC Power
                  <br />
                  Ports: USB-C, USB-A, DC5521, Cigarette Lighter, and Anderson
                  Port
                </Fragment>
              }
              buttonText="BLUETTI"
              buttonIcon="Bluetti"
            />
          </li>
          <li>
            <PhotoListItem
              affiliateData={affiliateMerchants.bluetti}
              href="https://www.bluettipower.com/products/ac200l/20a-ac-charging-cable.webp"
              img="/images/products/bluetti/d40/front.webp"
              title="D40 DC-DC Charger"
              description={
                <Fragment>
                  400W alternator charger + integrate 12V house batteries
                </Fragment>
              }
              buttonText="BLUETTI"
              buttonIcon="Bluetti"
            />
          </li>
          <li>
            <PhotoListItem
              affiliateData={affiliateMerchants.bluetti}
              href="https://www.bluettipower.com/products/20a-ac-charging-cable-for-ac200l?variant=45914319519963"
              img="/images/products/bluetti/ac200l/20a-ac-charging-cable.webp"
              title="20A AC Charging Cable"
              description={
                <Fragment>Aviation (3-pin) to NEMA 5-20P male</Fragment>
              }
              buttonText="BLUETTI"
              buttonIcon="Bluetti"
            />
          </li>
        </ul>
        <br />

        <SlugHeading tag="h3">Included Accessories</SlugHeading>
        <p>
          Its nice they included all the accessory cables so you dont have to
          buy them separately. AC200L PV input cord is only 14 AWG. I would have
          expected 12AWG.
        </p>
        <ul>
          <li>
            <Link
              affiliateData={affiliateMerchants.bluetti}
              href="https://www.bluettipower.com/products/15a-ac-charging-cable-for-ac200l"
            >
              15A AC Charging Cable
            </Link>{" "}
            74 in (1,880 mm) -{" "}
            <span style="opacity:0.7;">
              Aviation (3-pin) to NEMA 5-15P male
            </span>
          </li>
          <li>
            <Link
              affiliateData={affiliateMerchants.bluetti}
              href="https://www.bluettipower.com/products/aviation-plug-to-xt90-cable?variant=47477804040411"
            >
              DC Input Cable
            </Link>{" "}
            42 in (1,067 mm) -{" "}
            <span style="opacity:0.7;">
              Aviation (2-pin) to female XT90 (if ordering a replacement, get
              the blue version)
            </span>
          </li>
          <li>
            <Link
              affiliateData={affiliateMerchants.bluetti}
              href="https://www.bluettipower.com/products/car-charging-cable-xt90-cigarette"
            >
              Car Charging Cable
            </Link>{" "}
            24 in (610 mm) -{" "}
            <span style="opacity:0.7;">Male XT90 to male 12V socket.</span>
          </li>
          <li>
            <Link
              affiliateData={affiliateMerchants.bluetti}
              href="https://www.bluettipower.com/products/solar-charging-cable-xt90-to-mc4-cable"
            >
              Solar Charging Cable
            </Link>{" "}
            31 in (790 mm) - <span style="opacity:0.7;">Male XT90 to MC4.</span>
          </li>
        </ul>
      </Center>
      {/* <Center>
        <SlugHeading tag="h2">Areas for Improvement</SlugHeading>

        <p>
          Hoping this feedback can help improve future revisions and updates:
        </p>
        <ul>
          <li>
            <strong>Remote control screen:</strong> For anyone installing the
            AC200L in a cabinet or under a bed, an optional remote screen would
            be extremely useful. Being able to toggle AC and DC power or check
            capacity without relying on Bluetooth or opening a cabinet would
            make daily use much easier.
          </li>
          <li>
            <strong>Integrated high-amp DC output:</strong> Right now, higher DC
            loads require the separate DC Hub and Charger 2, which takes up
            space and adds significant cost. A built‑in high‑amp Anderson port
            would make it easier to run DC appliances directly from the AC200L
            without extra hardware.
          </li>
          <li>
            <strong>Additional solar port:</strong> If you rely on both
            alternator charging and rooftop solar, the AC200L’s single solar
            input can be limiting. You will need BLUETTI’s Charger 2, or another
            alternator charger that has solar input, to run both at the same
            time. A second built‑in solar port would make the system more
            flexible for van and RV setups and reduce the need for specialized
            hardware.
          </li>
          <li>
            <strong>90-degree cables:</strong> The side‑mounted ports can be
            awkward in tight spaces. Offering 90‑degree cable options would help
            with cable routing and make it easier to fit the AC200L into compact
            spaces while still supporting expansion batteries.
          </li>
          <li>
            <strong>More USB ports:</strong> Modern builds rely heavily on
            phones, tablets, cameras, and small electronics. A few more USB
            ports would reduce the need for external hubs and make the AC200L
            more convenient for everyday use.
          </li>
          <li>
            <strong>Self-heating version:</strong> For winter camping or
            cold‑weather travel, a self‑heating battery option would be a major
            upgrade. It would allow safe charging in freezing temperatures
            without needing to warm the unit manually.
          </li>
          <li>
            <strong>Direct XT90 input:</strong> The current aviation‑to‑XT90
            adapter adds an extra connection point and cable that does not need
            to exist. A native XT90 port would clean up the wiring, reduce
            clutter, and make the system easier to integrate with various
            setups.
          </li>
        </ul>
      </Center> */}
      {/* <Center>
        <SlugHeading tag="h2">Personal Experiece</SlugHeading>
        <ul>
          <li>
            <strong>AC input:</strong> Had no issues powering my 1800W induction
            cooktop.
          </li>
          <li>
            <strong>App:</strong> Decent but not as intuitive as it could be.
            DC/USB are separatley controled on the device but a single button in
            the app. I love that you can control the USB ports as EcoFlow
            doesn't support this but grouping it together in the app is
            terrible. I want the ability to turn of DC but keep USB on.
          </li>
          <li>
            <strong>Lack of native 30A+ DC output:</strong> Results in having to
            buy additional devices and takes up more space than it should.
          </li>
          <li>
            <strong>Port placement & cables:</strong> Wastes a lot of unessasary
            space and wasn't the best fit for my specific van build.
          </li>
          <li>
            <strong>Device control:</strong> I'm used to having a separate
            control screen for my EcoFlow setup which I use frequently to turn
            AC on/off and monitor my capacity and input. Since the power station
            is not very accessible in my build I had to use the app which added
            an extra friction point instead of a quick glance or tapping a
            button.
          </li>
        </ul>
      </Center> */}
      <Center>
        <SlugHeading tag="h2">My Verdict</SlugHeading>
        <p>
          The <strong>BLUETTI AC200L</strong> delivers strong performance for
          the price, offering fast charging, high‑voltage solar support, and
          solid expandability that make it a dependable foundation for many
          vanlife and RV setups. Its modular approach to high‑amp DC output adds
          cost and extra components, but the overall system remains flexible,
          capable, and well‑suited for builders who want room to grow without
          jumping to a larger, more expensive setups.
        </p>
        <p>
          While the hardware is capable, the integration with the Charger 2 and
          DC Hub is currently held back by firmware issues. In my testing,
          integration with these accessories lacked some expected functionality.
          If you plan to use this as a standalone unit or with basic solar, it
          is a great option. If you are building a complete system around the DC
          Hub, you should make sure you are aware of whether these issues will
          impact you.
        </p>
        {/* <p>
          <strong>The Bottom Line:</strong> The AC200L is a high‑value,
          high‑performance foundation for modular vanlife power systems,
          provided you are fine adding the accessories needed to unlock its full
          potential.
        </p> */}
        <p>
          <strong>The Bottom Line:</strong> The AC200L is a high performance
          foundation for modular systems, provided you are comfortable with the
          current firmware limitations of the accessories needed to unlock its
          full potential.
        </p>

        <BluettiCouponCode
          mini
          href="https://www.bluettipower.com/products/ac200l-portable-power-station"
          buttonText="Buy from BLUETTI"
        />

        <p>
          Not sure it’s the best fit? My{" "}
          <Link href={namedRoutes.powerSystems}>power systems guide</Link>{" "}
          covers all the popular brands.
        </p>

        <ButtonList>
          <Button
            affiliateData={affiliateMerchants.bluetti}
            href="https://www.bluettipower.com/products/ac200l-portable-power-station"
            icon="Bluetti"
          >
            Buy from BLUETTI
          </Button>
          <Button
            affiliate="https://amzn.to/3Q6jLIN"
            href="https://www.amazon.com/dp/B0CLGZB3L6/"
            secondary
            icon="Amazon"
          >
            Buy on Amazon
          </Button>
        </ButtonList>

        {/* <p>
          <strong>Still unsure?</strong> Check out{" "}
          <Link href={namedRoutes.powerStations}>other power stations</Link> to
          power your setup.
        </p> */}

        <Tags tags={headerInfo.tags} brands={headerInfo.brands} />
      </Center>
      <Center>
        <h3>Related</h3>
        <ul class="none">
          <li>
            <ReviewItem href={namedRoutes.bluetti.rv5} />
          </li>
          <li>
            <ReviewItem href={namedRoutes.alternatorChargers} />
          </li>
          <li>
            <ReviewItem href={namedRoutes.inventory.vanlife} />
          </li>
        </ul>
      </Center>
      <Questions />
      <Center>
        <Author
          author
          published={headerInfo.datePublished}
          updated={headerInfo.dateModified}
        />
      </Center>
      <StickyButtons
        title="BLUETTI AC200L"
        description={
          <Fragment>
            ${price.current}{" "}
            {price.current < price.msrp ? <s>${price.msrp}</s> : null}
          </Fragment>
        }
      >
        <Button
          affiliateData={affiliateMerchants.bluetti}
          href="https://www.bluettipower.com/products/ac200l-portable-power-station"
          icon="Bluetti"
        >
          View on BLUETTI
        </Button>
        <Button
          affiliate="https://amzn.to/3Q6jLIN"
          href="https://www.amazon.com/dp/B0CLGZB3L6/"
          secondary
          icon="Amazon"
        >
          View on Amazon
        </Button>
      </StickyButtons>
    </Editorial>
  </div>
);

export default Page;
